import { apiClient } from '../../../../../services/apiClient'
import type { LandSearchRequest as NewLandSearchRequest } from '../types/landFilters.types'

// Import shared types from the lib
import type {
  Land,
  LandSearchFilters,
  LandSearchRequest,
  LandSearchResponse,
  GeoPoint,
} from '@popety-io/types'

// Re-export for backward compatibility
export type { GeoPoint, LandSearchFilters, LandSearchRequest }

// Pagination params for frontend use
export interface LandSearchParams {
  page?: number
  limit?: number
  sort?: string
  order?: 'asc' | 'desc'
}

// Use the shared Land type from lib
export type BackendLand = Land
export type LandProperty = Land

// Essential fields for list view display
const LIST_VIEW_FIELDS = [
  // Identifiers
  'land_id',
  'egrid_nb',

  // Location
  'address_full',
  'municipality_name',
  'region_name',
  'address_postal_code_name',

  // Basic property info
  'area',
  'type',

  // Building details
  'building_nb',
  'unit_nb',
  'average_building_year',

  // Zoning/planning
  'main_lupa_name',
  'main_lupa_type',
  'main_lupa_cover_area',
  'cos',
  'cus',
  'lupa_number',

  // Scores for radar chart
  'development_score',
  'under_exploited_score',
  'poi_score',
  'livability_score',
  'existing_score',

  // Activity data
  'listings',
  'transactions',
  'construction_permits',
  'transaction_number',
  'listing_rent_number',
  'listing_sale_number',
  'construction_permit_number',

  // Links
  'rdppf_land_link',
  'geo_portal_link',
  'rf_uri',

  // Other
  'city_ofs_number',
  'code_number',
]

/**
 * Transform frontend filter format to API format
 * Converts nested { min, max } objects to flat _min/_max fields
 */
const transformFiltersToAPIFormat = (
  frontendFilters: any
): LandSearchFilters => {
  const apiFilters: LandSearchFilters = { ...frontendFilters }

  // Transform unit_nb: { min, max } -> unit_nb_min, unit_nb_max
  if (frontendFilters.unit_nb) {
    if (frontendFilters.unit_nb.min !== undefined) {
      apiFilters.unit_nb_min = frontendFilters.unit_nb.min
    }
    if (frontendFilters.unit_nb.max !== undefined) {
      apiFilters.unit_nb_max = frontendFilters.unit_nb.max
    }
    delete (apiFilters as any).unit_nb
  }

  // Transform sb: { min, max } -> sb_min, sb_max (gross floor area)
  if (frontendFilters.sb) {
    if (frontendFilters.sb.min !== undefined) {
      apiFilters.sb_min = frontendFilters.sb.min
    }
    if (frontendFilters.sb.max !== undefined) {
      apiFilters.sb_max = frontendFilters.sb.max
    }
    delete (apiFilters as any).sb
  }

  // Transform building_area: { min, max } -> official_area_min, official_area_max
  if (frontendFilters.building_area) {
    if (frontendFilters.building_area.min !== undefined) {
      apiFilters.official_area_min = frontendFilters.building_area.min
    }
    if (frontendFilters.building_area.max !== undefined) {
      apiFilters.official_area_max = frontendFilters.building_area.max
    }
    delete (apiFilters as any).building_area
  }

  // Transform floor_number: { min, max } -> max_floor_nb_min, max_floor_nb_max
  if (frontendFilters.floor_number) {
    if (frontendFilters.floor_number.min !== undefined) {
      apiFilters.max_floor_nb_min = frontendFilters.floor_number.min
    }
    if (frontendFilters.floor_number.max !== undefined) {
      apiFilters.max_floor_nb_max = frontendFilters.floor_number.max
    }
    delete (apiFilters as any).floor_number
  }

  // Transform building_height: { min, max } -> max_height_min, max_height_max
  if (frontendFilters.building_height) {
    if (frontendFilters.building_height.min !== undefined) {
      apiFilters.max_height_min = frontendFilters.building_height.min
    }
    if (frontendFilters.building_height.max !== undefined) {
      apiFilters.max_height_max = frontendFilters.building_height.max
    }
    delete (apiFilters as any).building_height
  }

  // Transform volume: { min, max } - Note: API may need volume_min/volume_max or similar
  // For now, we'll keep volume as-is since the API may handle it differently

  return apiFilters
}

export const landSearchService = {
  search: async (
    requestBody: LandSearchRequest
  ): Promise<LandSearchResponse> => {
    const response = await apiClient.post('/lands/search', requestBody)
    return response.data
  },

  // New search method for the new filter format
  searchWithNewFilters: async (
    request: NewLandSearchRequest
  ): Promise<LandSearchResponse> => {
    // Transform frontend filters to API format
    const apiFilters = transformFiltersToAPIFormat(request.filters)

    const requestBody: LandSearchRequest = {
      filters: apiFilters,
      fields: request.fields || LIST_VIEW_FIELDS,
      limit: request.limit,
      offset: request.offset,
      sort: request.sort,
    }

    return landSearchService.search(requestBody)
  },
}
